import React, { useState, createContext, useContext, useEffect } from 'react'
import { useCalendlyEventListener } from 'react-calendly'
import { apiGet, apiPatch, apiPost } from '../../util/api'
import _ from 'lodash'
import useServiceRequestForm from './ProfessionalServices/useServiceRequestForm'
import { store } from '../../lib/store'
import { setGoal } from '../ActionPlan/ActionPlanSlice'
import { useTranslation } from '~/hooks/useTranslation'
import { useToast } from '~/lib/ui/toast'

const BenefitsContext = createContext()

export const useBenefits = () => {
  return useContext(BenefitsContext)
}

const BenefitsProvider = ({
  current_user,
  coach,
  service_requests,
  calendly_events_path,
  resume_review,
  resume_revisions,
  industries,
  revenue_options,
  company_sizes,
  service_request_class,
  most_recent_resume,
  select_strengths_assessment_enabled,
  children,
}) => {
  const [serviceRequestList, setServiceRequestList] = useState(service_requests)
  const [showScheduleDialog, setShowScheduleDialog] = useState(false)
  const [showUpdateProfileDialog, setShowUpdateProfileDialog] = useState(false)
  const [showCloseReviewDialog, setShowCloseReviewDialog] = useState(false)
  const [showMessageCoachDialog, setShowMessageCoachDialog] = useState(false)
  const [serviceRequestType, setServiceRequestType] = useState(
    service_request_class,
  )
  const [mostRecentResume, setMostRecentResume] = useState(most_recent_resume)
  const [isReformatting, setIsReformatting] = useState(false)
  const [isReformattingLoading, setIsReformattingLoading] = useState(false)
  const [calendlyOpen, setCalendlyOpen] = useState(false)
  // Check for new_request param in URL
  const hasViewPreviousParam =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('view_previous') === 'true'

  const [serviceRequest, setServiceRequest] = useState(
    (serviceRequestList &&
      !serviceRequestList[service_request_class]?.completed) ||
      serviceRequestList[service_request_class]?.completed_reoccurring_requests
        .length == 0
      ? serviceRequestList[service_request_class]
      : {
          completed_reoccurring_requests:
            serviceRequestList[service_request_class]
              ?.completed_reoccurring_requests,
        },
  )
  const [targetedResearchType, setTargetedResearchType] = useState(
    serviceRequest?.targeted_research_type ||
      serviceRequest?.['completed_reoccurring_requests'][
        serviceRequest?.['completed_reoccurring_requests'].length - 1
      ]?.targeted_research_type,
  )
  const [showBackButton, setShowBackButton] = useState(true)
  const [dialogTitle, setDialogTitle] = useState('')
  const [meetingViaCalendly, setMeetingViaCalendly] = useState(
    serviceRequest?.created_at ? serviceRequest?.meeting_via_calendly : true,
  )
  const [meetingDuration, setMeetingDuration] = useState(null)
  const [candidateRevisionIndex, setCandidateRevisionIndex] = useState(0)
  const [resumeReview, setResumeReview] = useState(resume_review)
  const [page, setPage] = useState(serviceRequest?.cancelled_by_coach ? 2 : 1)
  const [uploadedResumeId, setUploadedResumeId] = useState(null)
  const [resumeRevisions, setResumeRevisions] = useState(resume_revisions)
  const { t } = useTranslation()
  const { toast } = useToast()
  const candidateRevisions =
    resumeRevisions?.filter(
      (rev) => rev.type === 'ResumeRevision::CandidateRevision',
    ) || []
  // updateCandidateRevision - Boolean to determine if we are updating the revision object, if false we are updating resume review object.
  const updateCandidateRevision = candidateRevisions.length > 0
  const currentCandidateRevision = updateCandidateRevision
    ? candidateRevisions[0]
    : null
  const { select, isPremium, profileIncomplete } = current_user
  const selectStrengthsAssessmentEnabled = select_strengths_assessment_enabled

  const programLevel = isPremium ? 'Premium' : 'Flex'

  const coachRevisions = resumeRevisions?.filter(
    (revision) => revision.type === 'ResumeRevision::CoachRevision',
  )

  const toggleCloseReviewDialog = () =>
    setShowCloseReviewDialog(!showCloseReviewDialog)

  const attachErrorKeyByType = {
    'ServiceRequest::CoverLetterReview':
      'cover_letter_review_form.file_upload.candidate_document.could_not_attach_file',
    'ServiceRequest::StrengthsAssessment':
      'strengths_assessment_form.file_upload.document_id.could_not_attach_file',
  }

  const createServiceRequest = async () => {
    try {
      const data = {
        service_request: {
          ...serviceRequestData,
          meeting_via_calendly: meetingViaCalendly,
          type: serviceRequestType,
          ...(serviceRequestType === 'ServiceRequest::TargetedResearch' && {
            targeted_research_type: targetedResearchType,
          }),
        },
      }
      const res = await apiPost(
        '/coach_services/create_service_request',
        data,
        true,
      )

      if (res.status === 200) {
        setTimeout(() => {
          setServiceRequestList((prev) => ({
            ...prev,
            [serviceRequestType]: res.data.service_request,
          }))
          if (serviceRequestType === 'ServiceRequest::ResumeReview') {
            setResumeReview(res.data.service_request)
          }
          toggleShowScheduleDialog({ forceOpen: false })
        }, 2000)
        return res
      }

      throw new Error('Service request creation failed')
    } catch (error) {
      if (error?.response?.status === 422) {
        const translatedTitle = attachErrorKeyByType[serviceRequestType]

        toast({
          description: t(translatedTitle),
          variant: 'destructive',
        })
        setSubmitted(false)
        return error.response
      }

      throw error
    }
  }

  const {
    serviceRequestForm,
    onSubmit,
    clearForm,
    submitted,
    setSubmitted,
    handleBack,
    serviceRequestData,
  } = useServiceRequestForm({
    setPage,
    setShowBackButton,
    current_user,
    serviceRequestList,
    serviceRequestType,
    meetingViaCalendly,
    createServiceRequest,
    serviceRequest,
  })

  const { watch, control, setValue } = serviceRequestForm

  const message = watch('message')
  const linkedinUrl = watch('linkedin_url')
  const seniorityLevel = watch('seniority_level')
  const documentId = watch('document_id')
  const resumeId = watch('resume_document_id')
  const notes = watch('notes')
  const remote_content = watch('remote_content')
  const industry_role = watch('industry_role')
  const location = watch('location')
  const industry = watch('industry')
  const industry2 = watch('industry_2')
  const sector = watch('sector')
  const exampleTitle1 = watch('example_job_1')
  const exampleTitle2 = watch('example_job_2')
  const companySize = watch('company_size')
  const salary = watch('salary')
  const onboardingGoal = watch('desired_goal')
  const desiredTimeline = watch('desired_timeline')
  const targetRoles = watch('target_roles')
  const careerLevel = watch('career_level')
  const interviewTypes = watch('interview_types')
  const mostRecentRole = watch('most_recent_role')
  const desiredRole = watch('desired_role')
  const targetJobDescription = watch('target_job_description')
  const generalTopic = watch('general_topic')
  const exampleJob1 = watch('example_job_1')
  const exampleJob2 = watch('example_job_2')
  const phone_number = watch('phone_number')
  const email = watch('email')
  const colors = watch('colors')
  const style = watch('style')
  const exampleCompany1 = watch('example_company_1')
  const exampleCompany2 = watch('example_company_2')
  const workExperience = watch('work_experience')
  const educationHistory = watch('education_history')
  const professionalName = watch('professional_name')

  const toggleShowScheduleDialog = ({ forceOpen, resetForm = true } = {}) => {
    // forceOpen controls explicit behavior:
    // - true: open the modal
    // - false: close the modal
    // - undefined/null: toggle based on previous state
    setShowScheduleDialog((prev) => forceOpen ?? !prev)
    if (resetForm) {
      setTimeout(() => {
        clearForm()
      }, 0)
    }
  }

  const toggleShowUpdateProfileDialog = () => {
    setShowUpdateProfileDialog(!showUpdateProfileDialog)
    setShowScheduleDialog(false)
  }

  useEffect(() => {
    if (uploadedResumeId !== null) {
      serviceRequestForm.setValue('resume_document_id', uploadedResumeId)
    }
  }, [serviceRequestForm, uploadedResumeId])

  useEffect(() => {
    if (serviceRequestList && serviceRequestList[serviceRequestType]) {
      !serviceRequestList[serviceRequestType].completed ||
      serviceRequestList[serviceRequestType].completed_reoccurring_requests
        .length == 0
        ? setServiceRequest(serviceRequestList[serviceRequestType])
        : setServiceRequest({
            completed_reoccurring_requests:
              serviceRequestList[serviceRequestType]
                ?.completed_reoccurring_requests,
          })
    }
  }, [serviceRequestList, serviceRequestType, service_request_class])

  const onScheduleClick = (
    type,
    cardTitle,
    meetingViaCalendly,
    page = 1,
    coachRevisionCount = 1,
  ) => {
    setServiceRequestType(type)
    setDialogTitle(cardTitle)
    setMeetingViaCalendly(meetingViaCalendly)
    setCandidateRevisionIndex(coachRevisionCount - 1)
    setPage(page)
    setShowScheduleDialog(true)
  }

  const onCancelClick = () => {
    let intervalId

    const fetchCalendlyEvents = async () => {
      try {
        const res = await apiGet('/coach_services/check_calendly_events')
        if (res.status === 200) {
          const newServiceRequestList = res.data.service_requests
          const newResumeRevisions = res.data.resume_revisions
          if (
            serviceRequestList &&
            !_.isEqual(JSON.parse(newServiceRequestList), serviceRequestList)
          ) {
            setServiceRequestList(JSON.parse(newServiceRequestList))
            clearInterval(intervalId)
          }
          if (
            resumeRevisions &&
            !_.isEqual(JSON.parse(newResumeRevisions), resumeRevisions)
          ) {
            setResumeRevisions(JSON.parse(newResumeRevisions))
            clearInterval(intervalId)
          }
        }
      } catch (error) {
        console.error('Error fetching Calendly events:', error)
      }
    }

    intervalId = setInterval(fetchCalendlyEvents, 20000)

    return () => clearInterval(intervalId)
  }

  const onDeclineRevisionClick = async (requestId) => {
    try {
      const res = await apiPost('/coach_services/decline_revision', {
        id: requestId,
      })
      if (res.status === 200) {
        setResumeReview(res.data.resume_review)
        toggleCloseReviewDialog()
      }
    } catch (error) {
      console.error('Error fetching Calendly events:', error)
    }
  }

  useCalendlyEventListener({
    onEventScheduled: (e) => {
      const payload = e.data.payload
      setShowBackButton(false)
      setCalendlyOpen(false)
      if (!isReformatting) {
        toggleShowScheduleDialog({ forceOpen: false, resetForm: false })
      }
      createCalendlyEvent(
        payload.event.uri.split('/').pop(),
        payload.invitee.uri.split('/').pop(),
      )
    },
  })

  const changeReviewFormat = async ({ newFormat }) => {
    setShowScheduleDialog(false)
    setIsReformattingLoading(true)
    try {
      const response = await apiPatch(
        `/coach_services/change_resume_review_format`,
        {
          candidate_id: current_user.candidate_id,
          new_format: newFormat,
          update_candidate_revision: updateCandidateRevision,
        },
      )
      if (response.status === 200) {
        const { resume_review, resume_revisions, event } = response.data || {}
        if (resume_review) {
          setResumeReview(resume_review)
          setServiceRequestList((prev) => {
            const updated = {
              ...prev,
              ['ServiceRequest::ResumeReview']: {
                ...resume_review,
                ...(resume_review.meeting_via_calendly && event
                  ? { calendly_event: event }
                  : {}),
              },
            }
            return updated
          })
        }
        if (resume_revisions) {
          setResumeRevisions(resume_revisions)
        }
      } else {
        console.log('Failed to change review format.')
      }
    } catch (err) {
      console.log('Failed to change review format.', err)
    } finally {
      setIsReformatting(false)
      setIsReformattingLoading(false)
    }
  }

  const changeResume = async (serviceRequestId) => {
    const data = {
      service_request: { document_id: resumeId },
    }
    const res = await apiPatch(
      `/coach_services/update_resume_document/${serviceRequestId}`,
      data,
    )

    if (res.status === 200) {
      setTimeout(() => {
        setResumeReview(res.data.resume_review)
        toggleShowScheduleDialog({ forceOpen: false })
      }, 2000)
      return res
    } else {
      throw new Error('Service request creation failed')
    }
  }

  const createRevisionRequest = async () => {
    const revisionRequestPayload = {
      ...serviceRequestData,
    }

    const res = await apiPost(
      '/coach_services/create_revision_request',
      {
        service_request: revisionRequestPayload,
      },
      true,
    )

    if (res.status === 200) {
      setTimeout(() => {
        setResumeReview(res.data.resume_review)
        setResumeRevisions(res.data.resume_revisions)
        toggleShowScheduleDialog({ forceOpen: false })
      }, 2000)
      return res
    } else {
      throw new Error('Service request creation failed')
    }
  }

  const createCalendlyEvent = async (calendlyId, calendlyUserId) => {
    if (isReformatting) {
      setIsReformattingLoading(true)
    }
    const data = {
      request_format: 'json',
      calendly_event: {
        candidate_id: current_user.candidate_id,
        coach_id: coach.id,
        calendly_id: calendlyId,
        calendly_user_id: calendlyUserId,
        service_request_type: serviceRequestType,
        service_request_data: {
          ...serviceRequestData,
          candidate_revision_index: candidateRevisionIndex,
        },
        ...(resumeReview?.id && { service_request_id: resumeReview.id }),
        ...(isReformatting && { meeting_format_changed: true }),
        ...(isReformatting && {
          service_request_revision_id:
            currentCandidateRevision?.id || coachRevisions?.[0]?.id,
        }),
      },
    }

    const res = await apiPost(calendly_events_path, data, true)

    if (res.status === 200) {
      setTimeout(async () => {
        const event = res.data.calendlyEvent
        const revisions = res.data.resumeRevisions
        const review = res.data.resumeReview

        if (!isReformatting) {
          setResumeReview(review)
          if (
            serviceRequestType === 'ResumeRevision::CandidateRevision' &&
            revisions?.length > 0
          ) {
            setResumeRevisions(revisions)
          } else {
            setServiceRequestList((prev) => ({
              ...prev,
              [serviceRequestType]: {
                ...res.data.service_request,
                calendly_event: event,
              },
            }))
          }
        }
        if (res.data.service_request.tracker_goal) {
          store.dispatch(setGoal(res.data.service_request.tracker_goal))
        }
        if (isReformatting) {
          await changeReviewFormat({ newFormat: 'calendly' })
        }
        clearForm()
      }, 2000)
      return res
    } else {
      throw new Error('Calendly event creation failed')
    }
  }

  const values = {
    current_user,
    coach,
    showScheduleDialog,
    toggleShowScheduleDialog,
    showUpdateProfileDialog,
    toggleShowUpdateProfileDialog,
    serviceRequestType,
    setServiceRequestType,
    onScheduleClick,
    onCancelClick,
    onDeclineRevisionClick,
    serviceRequestForm,
    serviceRequestList,
    showBackButton,
    dialogTitle,
    message,
    linkedinUrl,
    createServiceRequest,
    meetingViaCalendly,
    setMeetingViaCalendly,
    seniorityLevel,
    resumeId,
    documentId,
    uploadedResumeId,
    setUploadedResumeId,
    resumeReview,
    setResumeReview,
    page,
    handleBack,
    control,
    onSubmit,
    submitted,
    resumeRevisions,
    candidateRevisions,
    updateCandidateRevision,
    currentCandidateRevision,
    createRevisionRequest,
    notes,
    remote_content,
    select,
    industries,
    industry_role,
    location,
    revenue_options,
    company_sizes,
    industry,
    industry2,
    sector,
    exampleTitle1,
    exampleTitle2,
    companySize,
    salary,
    mostRecentRole,
    desiredRole,
    setDialogTitle,
    setPage,
    setShowScheduleDialog,
    setValue,
    showCloseReviewDialog,
    setShowCloseReviewDialog,
    toggleCloseReviewDialog,
    changeResume,
    serviceRequest,
    setServiceRequest,
    showMessageCoachDialog,
    setShowMessageCoachDialog,
    onboardingGoal,
    desiredTimeline,
    targetRoles,
    careerLevel,
    interviewTypes,
    hasViewPreviousParam,
    coachRevisions,
    programLevel,
    targetJobDescription,
    isPremium,
    profileIncomplete,
    generalTopic,
    exampleJob1,
    exampleJob2,
    targetedResearchType,
    setTargetedResearchType,
    phone_number,
    email,
    colors,
    style,
    exampleCompany1,
    exampleCompany2,
    workExperience,
    educationHistory,
    professionalName,
    mostRecentResume,
    setMostRecentResume,
    selectStrengthsAssessmentEnabled,
    isReformatting,
    setIsReformatting,
    isReformattingLoading,
    setIsReformattingLoading,
    changeReviewFormat,
    calendlyOpen,
    setCalendlyOpen,
    meetingDuration,
    setMeetingDuration,
  }
  return (
    <BenefitsContext.Provider value={values}>
      {children}
    </BenefitsContext.Provider>
  )
}
export default BenefitsProvider
